import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import axios from "axios";
import { Progress } from "@/components/ui/progress";
import { useEmbeddingStatus } from "@/hooks/useEmbeddingStatus";

// Define the backend API URL
const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3000"; // Fallback to localhost if env var is not set

const UploadResume = () => {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [resumeId, setResumeId] = useState<number | null>(null);
  const { toast } = useToast();

  // Use our custom hook to manage embedding status
  const { status: embeddingStatus, progress } = useEmbeddingStatus({
    resumeId,
    initialStatus: "not_started",
    pollingInterval: 3000,
  });

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setFile(event.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast({
        variant: "destructive",
        title: "No file selected",
        description: "Please select a file to upload.",
      });
      return;
    }

    setUploading(true);

    const formData = new FormData();
    formData.append("resume", file);

    try {
      // Using axios for the upload
      const response = await axios.post(
        `${API_URL}/api/upload-resume`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          // Include credentials if needed
          withCredentials: true,
        }
      );

      toast({
        title: "Upload Success!",
        description:
          "Resume uploaded successfully. Now generating embeddings...",
      });

      setResumeId(response.data.resumeId);

      setFile(null);
      // Reset the file input
      const fileInput = document.querySelector(
        'input[type="file"]'
      ) as HTMLInputElement;
      if (fileInput) fileInput.value = "";
    } catch (error) {
      console.error(error);

      let errorMessage = "Failed to upload resume.";
      if (axios.isAxiosError(error) && error.response) {
        // Extract error message from response if available
        errorMessage = error.response.data?.message || errorMessage;
      }

      toast({
        variant: "destructive",
        title: "Upload failed",
        description: errorMessage,
      });
    } finally {
      setUploading(false);
    }
  };

  // Helper function to render status message
  const renderStatusMessage = () => {
    switch (embeddingStatus) {
      case "processing":
        return (
          <div className="mt-4">
            <p className="text-sm text-primary mb-2">
              Generating embeddings... This may take a minute.
            </p>
            <Progress value={progress} className="h-2" />
            <p className="text-xs text-muted-foreground mt-1">
              You can start chatting once embeddings are complete.
            </p>
          </div>
        );
      case "completed":
        return (
          <div className="mt-4">
            <p className="text-sm text-green-600 mb-2">
              Embeddings complete! Your resume is ready.
            </p>
            <Progress value={100} className="h-2 bg-green-200" />
          </div>
        );
      case "error":
        return (
          <div className="mt-4">
            <p className="text-sm text-red-600 mb-2">
              Error generating embeddings. Please try uploading again.
            </p>
            <Progress value={100} className="h-2 bg-red-200" />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="container mx-auto py-10">
      <Card className="max-w-md mx-auto">
        <CardHeader>
          <CardTitle>Upload Your Resume</CardTitle>
          <CardDescription>
            Upload your resume to get started with the chatbot assistant.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid w-full items-center gap-4">
            <Input
              type="file"
              onChange={handleFileChange}
              accept=".pdf,.doc,.docx,.txt"
              className="cursor-pointer"
              disabled={uploading || embeddingStatus === "processing"}
            />
            {file && (
              <p className="text-sm text-muted-foreground">
                Selected file: {file.name}
              </p>
            )}
            {renderStatusMessage()}
          </div>
        </CardContent>
        <CardFooter>
          <Button
            onClick={handleUpload}
            disabled={uploading || !file || embeddingStatus === "processing"}
            className="w-full"
          >
            {uploading
              ? "Uploading..."
              : embeddingStatus === "completed"
              ? "Upload Another Resume"
              : "Upload Resume"}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default UploadResume;
